/**
 * Hooks TanStack Query para Nómina / RRHH.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../../lib/apiClient'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AlertaDelEmpleado {
  codigo: string
  nivel: 'danger' | 'warning' | 'info'
  mensaje: string
}

export interface Employee {
  id: string; rut: string; first_name: string; last_name: string; full_name: string
  birth_date?: string; gender?: string; marital_status?: string; nationality?: string
  family_dependents?: number
  /** URL firmada de vida corta (nunca la key). La cambia RRHH por `employees/<id>/photo/`. */
  photo_url?: string | null
  /** Novedades de un vistazo (solo en el DETALLE): de vacaciones, con licencia,
   *  contrato por vencer… Las calcula el backend (alertas_empleado.py). */
  alertas?: AlertaDelEmpleado[]
  worker_type?: string
  address?: string; phone?: string; email?: string
  cost_center?: string | null
  supervisor?: string | null
  backup_employee?: string | null
  payment_method?: string
  bank_name?: string
  bank_account_type?: string
  bank_account_number?: string
  hire_date: string; termination_date?: string; status: string
  /** Años con empleadores anteriores acreditados; suman al feriado progresivo (Art. 68). */
  prior_service_years?: number | string
  /** Fecha en que el trabajador acreditó el servicio anterior; el feriado
   *  progresivo que depende de esos años corre desde ahí (exigencia de la DT). */
  prior_service_accredited_on?: string | null
  /** Medio de prueba presentado: afp_certificate | labor_inspection |
   *  public_instrument | judicial. */
  prior_service_document?: string
  prior_service_document_display?: string
  current_position?: string; is_active: boolean; created_at: string
  has_current_contract?: boolean
  contract_expiry?: { expiring: boolean; end_date: string; days_left: number } | null
}

export interface EmploymentContract {
  id: string; employee: string; employee_name: string
  contract_type: string; contract_type_display: string
  start_date: string; end_date?: string; base_salary: string
  gratification_mode?: string; gratification_mode_display?: string; gratification_fixed_amount?: string
  position: string; department: string; work_hours_weekly: string
  is_current: boolean; created_at: string
}

export interface PayrollPeriod {
  id: string; year: number; month: number; status: string; status_display: string
  total_gross: string; total_net: string; total_employer_cost: string
  payslips_count?: number; accounting_status: string; created_at: string
  voucher?: string | null
  voucher_number?: string | null
}

export interface Payslip {
  id: string; period: string; employee: string; employee_name: string; employee_rut: string
  contract: string; status: string; period_label?: string
  days_worked?: number
  base_salary: string; gratification: string
  overtime_hours: string; overtime_amount: string
  overtime_holiday_hours?: string; overtime_holiday_amount?: string
  bonuses: any[]; total_bonuses: string; gross_salary: string
  /** Asignación familiar. Ya viene sumada en `gross_salary`. No imponible. */
  family_allowance?: string
  total_imponible: string; total_tributable: string
  afp_employee: string; health_employee: string; health_additional: string
  unemployment_employee: string; tax_amount: string; apv_amount: string
  cuenta2_amount: string; deposito_convenido_amount: string
  other_deductions: any[]; total_deductions: string; sobregiro?: string; net_salary: string
  afp_employer: string; unemployment_employer: string; mutual_amount: string; sanna_amount: string
  reform_individual_amount: string; reform_crp_amount: string; expectativa_vida_amount: string
  employer_total_cost: string; calculation_details: any
  vacation_balance?: {
    days_pending: number
    days_entitled_per_year: number
    years_of_service: number
    is_progressive: boolean
  }
  extra_sections?: {
    apv?: { amount: string; configured: boolean; regime?: string }
    ccaf?: { amount: string; configured: boolean; note?: string }
    absences?: Array<{
      leave_type: string
      leave_type_display: string
      start_date: string
      end_date: string
      days: number
      paid: boolean
    }>
  }
  company_info?: any
  employee_info?: any
}

export interface PayrollBonus {
  id: string; name: string; code: string; bonus_type: string
  default_amount: string; is_taxable: boolean; is_imponible: boolean; is_active: boolean
  /** '' = automático según el tipo · 'yes' · 'no' */
  prorate_by_days: string
  /** Lo que efectivamente va a pasar, resuelto por el backend. */
  prorates_by_days: boolean
  account: string | null; account_code: string | null; account_name: string | null
}

export interface PayrollIndicator {
  id: string; year: number; month: number
  uf_value: string; utm_value: string; imm_value: string
  tope_afp_uf: string; tope_salud_uf: string; tope_cesantia_uf: string
}

export interface VacationRequest {
  id: string; employee: string; employee_name: string; vacation_record: string
  start_date: string; end_date: string; days_requested: string
  status: string; status_display: string; notes: string
}

export interface Termination {
  id: string; employee: string; employee_name: string; termination_date: string
  reason: string; reason_display: string
  severance_years: string; severance_amount: string; vacation_payout: string
  notice_period_payout: string; total_settlement: string
  status: string; notes: string; calculation_details: any
}

// ─── Employees ──────────────────────────────────────────────────────────────

export function useEmployees(params: { search?: string; status?: string; active?: string; page?: number; page_size?: number; ordering?: string }) {
  return useQuery({ queryKey: ['employees', params], queryFn: async () => { const r = await apiClient.get('/payroll/employees/', { params }); return r.data } })
}
export function useEmployee(id: string) {
  return useQuery({ queryKey: ['employees', id], queryFn: async () => { const r = await apiClient.get(`/payroll/employees/${id}/`); return r.data as Employee }, enabled: !!id })
}
/** Sube o reemplaza la foto del trabajador. La reprocesa el servidor: cuadrada, 512 px, sin metadatos. */
export function useUploadEmployeePhoto(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (foto: File) => {
      const fd = new FormData()
      fd.append('foto', foto)
      // El cliente declara `application/json` por defecto, y con eso axios convierte
      // el FormData a JSON: el archivo no llegaría. Las otras subidas lo declaran igual.
      const r = await apiClient.put(`/payroll/employees/${id}/photo/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return r.data as { photo_url: string | null }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  })
}
export function useRemoveEmployeePhoto(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => { await apiClient.delete(`/payroll/employees/${id}/photo/`) },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  })
}
export function useCreateEmployee() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: Partial<Employee>) => { const r = await apiClient.post('/payroll/employees/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }) })
}
export function useUpdateEmployee(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: Partial<Employee>) => { const r = await apiClient.patch(`/payroll/employees/${id}/`, d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }) })
}
/**
 * Como `useUpdateEmployee`, pero el id viaja al ejecutar la mutación. Sirve para
 * las acciones de una fila del listado: un hook no se puede crear dentro del clic
 * (#1458), y el del modal de edición está atado a OTRO empleado.
 */
export function usePatchEmployee() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...d }: Partial<Employee> & { id: string }) => {
      const r = await apiClient.patch(`/payroll/employees/${id}/`, d)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  })
}

// ─── Importación masiva de trabajadores (#21) ───────────────────────────────

export interface EmployeeImportField {
  key: string; label: string; required: boolean; aliases: string[]
  group: string; help: string
}

/**
 * Definición de columnas servida por el BACKEND, no declarada acá.
 * Con 30+ campos, tener la lista en las dos puntas garantiza que una quede atrás
 * y el auto-match deje de funcionar sin que se note.
 */
export function useEmployeeImportFields() {
  return useQuery({
    queryKey: ['employees', 'import-fields'],
    queryFn: async () => {
      const r = await apiClient.get('/payroll/employees/import/')
      return r.data.fields as EmployeeImportField[]
    },
    staleTime: Infinity,   // cambia solo con un deploy
  })
}

/** Lee encabezados y muestra en el servidor. Necesario para aceptar .xlsx. */
export function usePreviewEmployeeImport() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const r = await apiClient.post('/payroll/employees/import/preview/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return r.data as { headers: string[]; rows: string[][]; total_rows: number }
    },
  })
}

/**
 * Importa trabajadores. `dryRun` simula sin escribir: la UI lo usa para mostrar
 * el resumen y el cupo antes de que el usuario confirme.
 */
export function useImportEmployees() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, mapping, dryRun }: {
      file: File; mapping: Record<string, string>; dryRun: boolean
    }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('mapping', JSON.stringify(mapping))
      form.append('dry_run', dryRun ? 'true' : 'false')
      const r = await apiClient.post('/payroll/employees/import/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return r.data
    },
    onSuccess: (data) => {
      // Un dry-run no cambió nada: invalidar dejaría refetchear de gusto.
      if (!data?.dry_run) {
        qc.invalidateQueries({ queryKey: ['employees'] })
        qc.invalidateQueries({ queryKey: ['contracts'] })
      }
    },
  })
}

// ─── Portal del Colaborador (admin) ─────────────────────────────────────────

export interface PortalAccountStatus {
  has_account: boolean
  is_active: boolean | null
  is_activated: boolean | null
  email: string | null
  last_login_at: string | null
  has_erp_user: boolean
}

export function usePortalAccountStatus(employeeId: string) {
  return useQuery({
    queryKey: ['portal-account', employeeId],
    queryFn: async () => {
      const r = await apiClient.get(`/payroll/employees/${employeeId}/portal-account/`)
      return r.data as PortalAccountStatus
    },
    enabled: !!employeeId,
  })
}

export function useInvitePortal(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await apiClient.post(`/payroll/employees/${employeeId}/portal-account/`)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-account', employeeId] }),
  })
}

export function useDisablePortal(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await apiClient.delete(`/payroll/employees/${employeeId}/portal-account/`)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-account', employeeId] }),
  })
}

export function useResetPortalPassword(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await apiClient.post(`/payroll/employees/${employeeId}/portal-account/reset-password/`)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-account', employeeId] }),
  })
}

export interface BulkInviteResult {
  invited: number
  already_activated: number
  no_email: number
  erp_linked: number
  terminated: number
  /** Fichas cuyo correo ya es el acceso al portal de otra ficha (#1543). */
  email_in_use: number
  detail: string
}

export function useBulkInvitePortal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await apiClient.post('/payroll/employees/portal-account/bulk-invite/')
      return r.data as BulkInviteResult
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-account'] }),
  })
}

// ─── Contracts ──────────────────────────────────────────────────────────────

export function useContracts(params?: { employee_id?: string; search?: string; current?: string; active?: string; page_size?: number }) {
  return useQuery({ queryKey: ['contracts', params], queryFn: async () => { const r = await apiClient.get('/payroll/contracts/', { params }); return r.data } })
}
export function useCreateContract() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.post('/payroll/contracts/', d); return r.data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['contracts'] }); qc.invalidateQueries({ queryKey: ['employees'] }) } })
}
export function useUpdateContract(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.patch(`/payroll/contracts/${id}/`, d); return r.data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['contracts'] }); qc.invalidateQueries({ queryKey: ['employees'] }) } })
}

// ─── Previsional Config ─────────────────────────────────────────────────────

export interface PrevisionalConfig {
  id: string; employee: string; afp: string; afp_display: string; afp_rate: string
  health_system: string; health_system_display: string; isapre_name: string; isapre_display?: string; isapre_plan_uf: string
  /** Número del FUN del contrato de salud (campo 76 del Previred). Vacío en FONASA. */
  isapre_fun_number?: string
  isapre_fun?: string | null; isapre_fun_url?: string | null
  mutual: string; mutual_rate: string; cotiza_voluntariamente?: boolean; exento_reforma?: boolean
  es_pensionado?: boolean; suspende_afp_por_edad?: boolean
  apv_amount: string; apv_regime: string
  cuenta2_amount?: string; deposito_convenido_amount?: string
  is_current: boolean
}

export function usePrevisionalConfigs(params?: { employee_id?: string; current?: string }) {
  return useQuery({
    queryKey: ['previsional', params],
    queryFn: async () => { const r = await apiClient.get('/payroll/previsional/', { params }); return r.data as { configs: PrevisionalConfig[]; count: number } },
    enabled: !!params?.employee_id,
  })
}
export function useCreatePrevisionalConfig() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.post('/payroll/previsional/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['previsional'] }) })
}
export function useUpdatePrevisionalConfig(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.patch(`/payroll/previsional/${id}/`, d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['previsional'] }) })
}

// ─── Employee Bonuses ───────────────────────────────────────────────────────

export interface EmployeeBonusAssignment {
  id: string; employee: string; bonus: string; bonus_name: string; bonus_code: string
  amount: string; effective_amount: string; is_taxable: boolean; is_imponible: boolean
  effective_from?: string | null; effective_to?: string | null
  is_active: boolean
}

export function useEmployeeBonuses(params?: { employee_id?: string }) {
  return useQuery({
    queryKey: ['employee-bonuses', params],
    queryFn: async () => { const r = await apiClient.get('/payroll/employee-bonuses/', { params }); return r.data as { assignments: EmployeeBonusAssignment[]; count: number } },
    enabled: !!params?.employee_id,
  })
}
export function useCreateEmployeeBonus() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.post('/payroll/employee-bonuses/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['employee-bonuses'] }) })
}
export function useUpdateEmployeeBonus(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.patch(`/payroll/employee-bonuses/${id}/`, d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['employee-bonuses'] }) })
}
export function useDeleteEmployeeBonus(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async () => { await apiClient.delete(`/payroll/employee-bonuses/${id}/`); }, onSuccess: () => qc.invalidateQueries({ queryKey: ['employee-bonuses'] }) })
}

// ─── Employee Recurring Deductions ──────────────────────────────────────────

export interface RecurringDeduction {
  id: string; employee: string; employee_name?: string
  name: string; amount: string
  effective_from?: string | null; effective_to?: string | null
  is_active: boolean; notes?: string
}

export function useRecurringDeductions(params?: { employee_id?: string }) {
  return useQuery({
    queryKey: ['recurring-deductions', params],
    queryFn: async () => { const r = await apiClient.get('/payroll/employee-recurring-deductions/', { params }); return r.data as { deductions: RecurringDeduction[]; count: number } },
    enabled: !!params?.employee_id,
  })
}
export function useCreateRecurringDeduction() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.post('/payroll/employee-recurring-deductions/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['recurring-deductions'] }) })
}
/** Asignación masiva de descuento recurrente a varios empleados (o todos). */
export function useBulkAssignRecurringDeduction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (d: any) => { const r = await apiClient.post('/payroll/employee-recurring-deductions/bulk/', d); return r.data },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring-deductions'] })
      qc.invalidateQueries({ queryKey: ['employee-recurring-deductions'] })
    },
  })
}
/** Asignación masiva de un bono a varios empleados (o todos). */
export function useBulkAssignBonus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (d: any) => { const r = await apiClient.post('/payroll/employee-bonuses/bulk/', d); return r.data },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employee-bonuses'] }),
  })
}
export function useUpdateRecurringDeduction(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.patch(`/payroll/employee-recurring-deductions/${id}/`, d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['recurring-deductions'] }) })
}
export function useDeleteRecurringDeduction(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async () => { await apiClient.delete(`/payroll/employee-recurring-deductions/${id}/`); }, onSuccess: () => qc.invalidateQueries({ queryKey: ['recurring-deductions'] }) })
}

// ─── Period Inputs ──────────────────────────────────────────────────────────

export interface PeriodInput {
  id: string; period: string; employee: string; employee_name: string; employee_rut: string
  overtime_hours: string; bonus_overrides: any[]; extra_deductions: any[]; notes: string
}

export function usePeriodInputs(params?: { period_id?: string }) {
  return useQuery({
    queryKey: ['period-inputs', params],
    queryFn: async () => { const r = await apiClient.get('/payroll/period-inputs/', { params }); return r.data as { inputs: PeriodInput[]; count: number } },
    enabled: !!params?.period_id,
  })
}
export function useCreatePeriodInput() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.post('/payroll/period-inputs/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['period-inputs'] }) })
}
export function useUpdatePeriodInput(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.patch(`/payroll/period-inputs/${id}/`, d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['period-inputs'] }) })
}

// ─── Periods ────────────────────────────────────────────────────────────────

export function usePeriods() {
  return useQuery({ queryKey: ['payroll-periods'], queryFn: async () => { const r = await apiClient.get('/payroll/periods/'); return r.data as { periods: PayrollPeriod[]; count: number } } })
}
export function useCreatePeriod() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: { year: number; month: number }) => { const r = await apiClient.post('/payroll/periods/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-periods'] }) })
}
export function useCalculatePeriod(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async () => { const r = await apiClient.post(`/payroll/periods/${id}/calculate/`); return r.data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['payroll-periods'] }); qc.invalidateQueries({ queryKey: ['payslips'] }) } })
}
export function useApprovePeriod(id: string) {
  const qc = useQueryClient()
  // `notificarTrabajadores: false` aprueba sin enviar el correo de «tu liquidación
  // está disponible» (#312): es lo que hace falta al volver a aprobar tras un recálculo.
  return useMutation({
    mutationFn: async (opts?: { notificarTrabajadores?: boolean }) => {
      const r = await apiClient.post(`/payroll/periods/${id}/approve/`, {
        notificar_trabajadores: opts?.notificarTrabajadores ?? true,
      })
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-periods'] }),
  })
}
export interface PayPeriodPayload {
  payment_date?: string
  payment_account_id?: string
  payment_reference?: string
}
export function usePayPeriod(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload?: PayPeriodPayload) => {
      const r = await apiClient.post(`/payroll/periods/${id}/pay/`, payload || {})
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-periods'] }),
  })
}

// ─── Payslips ───────────────────────────────────────────────────────────────

export function usePayslips(params: { period_id?: string; employee_id?: string; page?: number; page_size?: number; ordering?: string }) {
  return useQuery({ queryKey: ['payslips', params], queryFn: async () => { const r = await apiClient.get('/payroll/payslips/', { params }); return r.data } })
}
export function usePayslip(id: string) {
  return useQuery({ queryKey: ['payslips', id], queryFn: async () => { const r = await apiClient.get(`/payroll/payslips/${id}/`); return r.data as Payslip }, enabled: !!id })
}

// ─── Bonuses ────────────────────────────────────────────────────────────────

export function useBonuses() {
  return useQuery({ queryKey: ['payroll-bonuses'], queryFn: async () => { const r = await apiClient.get('/payroll/bonuses/'); return r.data } })
}
export function useCreateBonus() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: Partial<PayrollBonus>) => { const r = await apiClient.post('/payroll/bonuses/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-bonuses'] }) })
}

// ─── Indicators ─────────────────────────────────────────────────────────────

export function useIndicators() {
  return useQuery({ queryKey: ['payroll-indicators'], queryFn: async () => { const r = await apiClient.get('/payroll/indicators/'); return r.data } })
}
export function useCreateIndicator() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: Partial<PayrollIndicator>) => { const r = await apiClient.post('/payroll/indicators/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-indicators'] }) })
}
/** Valores legales vigentes (IMM, topes imponibles, SIS) para prellenar el alta
 *  manual. Solo trae las claves con dato legal sembrado: lo que no venga se deja
 *  vacío en vez de sugerir una constante que envejece en el código. */
export function useIndicatorLegalDefaults(year?: number, month?: number) {
  return useQuery({
    queryKey: ['payroll-indicator-legal-defaults', year, month],
    enabled: !!year && !!month,
    queryFn: async () => {
      const r = await apiClient.get('/payroll/indicators/legal-defaults/', { params: { year, month } })
      return r.data as { defaults: Record<string, string> }
    },
  })
}
/** Un indicador del período con su estado y su procedencia. */
export interface PeriodIndicator {
  key: string
  label: string
  /** `used` = salió del snapshot del período ya calculado (histórico intocable),
   *  `available` = publicado y listo, `pending` = todavía no existe. */
  state: 'used' | 'available' | 'pending'
  value: string | null
  periodicity: 'monthly' | 'on_change' | 'derived' | null
  organismo: string
  source_url: string
  confidence: 'high' | 'medium' | 'low' | null
  /** Solo cuando está pendiente: cuándo se espera que el organismo lo publique. */
  expected_date: string | null
  /** El período se calculó con un valor distinto del legal de ese mes. */
  differs_from_legal: boolean
  legal_value: string | null
  unit?: string
  detalle?: Record<string, string | null> | null
  tramos?: Array<Record<string, string | null>>
  utm_base?: string | null
}

export interface IndicatorPeriod {
  year: number
  month: number
  period_status: string | null
  is_closed: boolean
  ready_to_calculate: boolean
  missing: string[]
  differs_from_legal: string[]
  caps: {
    apv_monthly_uf: string; apv_monthly_clp: string | null
    deposito_convenido_annual_uf: string; deposito_convenido_annual_clp: string | null
  }
  indicators: PeriodIndicator[]
}

/** Indicadores POR PERÍODO: qué se usó, qué está disponible y qué falta.
 *
 *  Para un período ya calculado los valores salen de su snapshot, así que la
 *  respuesta NO cambia si alguien corrige un indicador después. Sin `year`/`month`
 *  devuelve 13 períodos, del mes siguiente hacia atrás. */
export function useIndicatorPeriods(year?: number, month?: number) {
  return useQuery({
    queryKey: ['payroll-indicator-periods', year ?? null, month ?? null],
    queryFn: async () => {
      const params = year && month ? { year, month } : undefined
      const r = await apiClient.get('/payroll/indicators/periods/', { params })
      return r.data as { periods: IndicatorPeriod[] }
    },
  })
}

export function useUpdatePayrollIndicator(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: Partial<PayrollIndicator>) => { const r = await apiClient.patch(`/payroll/indicators/${id}/`, d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-indicators'] }) })
}
export function useDeletePayrollIndicator() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { await apiClient.delete(`/payroll/indicators/${id}/`) }, onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-indicators'] }) })
}

// ─── Family Allowance Brackets (Asignación Familiar) ─────────────────────────

export interface FamilyAllowanceBracket {
  id: string; valid_from: string; tramo: string; tramo_display?: string
  income_from: string; income_to: string | null; amount: string
  // Los mantiene la plataforma (viven en `public` desde la Fase 3): la norma que
  // fijó los montos y su fecha de publicación en el Diario Oficial. `published_on`
  // posterior a `valid_from` significa que el reajuste fue retroactivo.
  law_reference?: string; published_on?: string | null
  // El listado devuelve TODAS las vigencias —hace falta el histórico para explicar
  // un monto de un mes pasado— así que esta marca es la única forma de saber cuál
  // se está aplicando hoy.
  is_current?: boolean
}
export function useFamilyAllowanceBrackets() {
  return useQuery({ queryKey: ['family-allowance-brackets'], queryFn: async () => { const r = await apiClient.get('/payroll/family-allowance-brackets/'); return r.data as { brackets: FamilyAllowanceBracket[]; count: number } } })
}
export function useCreateFamilyAllowanceBracket() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: Partial<FamilyAllowanceBracket>) => { const r = await apiClient.post('/payroll/family-allowance-brackets/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['family-allowance-brackets'] }) })
}
export function useUpdateFamilyAllowanceBracket(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: Partial<FamilyAllowanceBracket>) => { const r = await apiClient.patch(`/payroll/family-allowance-brackets/${id}/`, d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['family-allowance-brackets'] }) })
}
export function useDeleteFamilyAllowanceBracket() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { await apiClient.delete(`/payroll/family-allowance-brackets/${id}/`) }, onSuccess: () => qc.invalidateQueries({ queryKey: ['family-allowance-brackets'] }) })
}

// ─── Vacations ──────────────────────────────────────────────────────────────

// `enabled` existe para la ficha del empleado: el listado exige el módulo
// `payroll.vacations` y la ficha vive en `payroll.employees`, así que quien no
// tenga el primero no debe ni disparar la petición — un 403 se vería igual que
// «esta persona no tiene vacaciones», que es el bug del #968.
/**
 * Permisos y licencias, filtrables por trabajador.
 *
 * Vive acá y no dentro de una pantalla porque lo consumen dos: la pestaña de
 * Vacaciones (todos los permisos del tenant) y la ficha del trabajador (los de
 * una persona). El `queryKey` es el mismo `['leave-requests', params]` que usaban
 * las mutaciones al invalidar, así que aprobar desde una pantalla refresca la otra.
 */
export function useLeaveRequests(
  params?: { employee_id?: string; leave_type?: string; status?: string },
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['leave-requests', params],
    queryFn: async () => { const r = await apiClient.get('/payroll/leaves/', { params }); return r.data },
    enabled: opts?.enabled ?? true,
  })
}

export function useVacationRequests(
  params?: { employee_id?: string; status?: string },
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['vacation-requests', params],
    queryFn: async () => { const r = await apiClient.get('/payroll/vacations/requests/', { params }); return r.data },
    enabled: opts?.enabled ?? true,
  })
}
export function useCreateVacationRequest() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: Partial<VacationRequest>) => { const r = await apiClient.post('/payroll/vacations/requests/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['vacation-requests'] }) })
}
export function useApproveVacation(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async () => { const r = await apiClient.post(`/payroll/vacations/requests/${id}/approve/`); return r.data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['vacation-requests'] }); qc.invalidateQueries({ queryKey: ['vacation-balance'] }) } })
}
/** Rechaza con motivo obligatorio: le llega por correo al trabajador (#1490). */
export function useRejectVacation(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (reason: string) => { const r = await apiClient.post(`/payroll/vacations/requests/${id}/reject/`, { reason }); return r.data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['vacation-requests'] }); qc.invalidateQueries({ queryKey: ['vacation-summary'] }) } })
}
/** Anula con motivo obligatorio y devuelve los días al saldo; avisa al trabajador. */
export function useCancelVacation(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (reason: string) => { const r = await apiClient.post(`/payroll/vacations/requests/${id}/cancel/`, { reason }); return r.data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['vacation-requests'] }); qc.invalidateQueries({ queryKey: ['vacation-balance'] }); qc.invalidateQueries({ queryKey: ['vacation-summary'] }) } })
}
export function useVacationBalance(employeeId: string) {
  return useQuery({ queryKey: ['vacation-balance', employeeId], queryFn: async () => { const r = await apiClient.get(`/payroll/vacations/balance/${employeeId}/`); return r.data }, enabled: !!employeeId })
}

// ─── Bolsas de vacaciones (períodos aniversario) ─────────────────────────────
// El saldo de una persona vive en sus bolsas: una legal y, si corresponde, una
// progresiva. `days_taken` NO se manda al escribir — lo mueve la aprobación de
// cada solicitud y el backend lo ignora.

export interface VacationRecordPayload {
  employee: string
  vacation_type: 'legal' | 'progressive'
  period_start: string
  period_end: string
  days_entitled: string
  status?: 'active' | 'closed'
}

function invalidarVacaciones(qc: ReturnType<typeof useQueryClient>, employeeId?: string) {
  qc.invalidateQueries({ queryKey: ['vacation-records'] })
  qc.invalidateQueries({ queryKey: ['vacation-balance', employeeId] })
  // El resumen de RRHH lista el saldo de toda la nómina: si no se invalida,
  // sigue mostrando el saldo viejo de quien se acaba de corregir.
  qc.invalidateQueries({ queryKey: ['vacation-summary'] })
}

export function useVacationRecords(employeeId: string, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['vacation-records', employeeId],
    queryFn: async () => {
      const r = await apiClient.get('/payroll/vacations/records/', { params: { employee_id: employeeId } })
      return r.data
    },
    enabled: (opts?.enabled ?? true) && !!employeeId,
  })
}

export function useCreateVacationRecord(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (d: VacationRecordPayload) => {
      const r = await apiClient.post('/payroll/vacations/records/', d); return r.data
    },
    onSuccess: () => invalidarVacaciones(qc, employeeId),
  })
}

export function useUpdateVacationRecord(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...d }: Partial<VacationRecordPayload> & { id: string }) => {
      const r = await apiClient.patch(`/payroll/vacations/records/${id}/`, d); return r.data
    },
    onSuccess: () => invalidarVacaciones(qc, employeeId),
  })
}

export function useDeleteVacationRecord(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`/payroll/vacations/records/${id}/`) },
    onSuccess: () => invalidarVacaciones(qc, employeeId),
  })
}

export interface BusinessDaysResult {
  business_days: number
  calendar_days: number
  weekend_days: number
  holiday_days: number
  holidays: { date: string; name: string; irrenunciable: boolean }[]
  saturday_inhabil: boolean
  error?: string
}
/** Calcula días hábiles entre dos fechas (excluye dom/sáb/feriados). */
export function useBusinessDays(start?: string, end?: string) {
  return useQuery<BusinessDaysResult>({
    queryKey: ['business-days', start, end],
    queryFn: async () => {
      const r = await apiClient.get('/payroll/business-days/', { params: { start, end } })
      return r.data
    },
    enabled: !!start && !!end && start <= end,
    staleTime: 5 * 60 * 1000,
  })
}

// ─── Feriados (ABM + sync) ────────────────────────────────────────────────────

export interface Holiday {
  id: string; country_code: string; holiday_date: string; name: string
  irrenunciable: boolean; kind: string; source: string; synced_at: string
}
export function useHolidays(params?: { year?: number; country_code?: string }) {
  return useQuery({ queryKey: ['holidays', params], queryFn: async () => { const r = await apiClient.get('/payroll/holidays/', { params }); return r.data as { holidays: Holiday[]; count: number } } })
}
export function useCreateHoliday() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: Partial<Holiday>) => { const r = await apiClient.post('/payroll/holidays/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['holidays'] }) })
}
export function useDeleteHoliday() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (id: string) => { await apiClient.delete(`/payroll/holidays/${id}/`) }, onSuccess: () => qc.invalidateQueries({ queryKey: ['holidays'] }) })
}
export function useSyncHolidays() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async () => { const r = await apiClient.post('/payroll/holidays/sync/'); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['holidays'] }) })
}

// ─── Terminations ───────────────────────────────────────────────────────────

export function useTerminations() {
  return useQuery({ queryKey: ['terminations'], queryFn: async () => { const r = await apiClient.get('/payroll/terminations/'); return r.data } })
}
export function useCreateTermination() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: Partial<Termination>) => { const r = await apiClient.post('/payroll/terminations/', d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['terminations'] }) })
}
export function useCalculateTermination(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async () => { const r = await apiClient.post(`/payroll/terminations/${id}/calculate/`); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['terminations'] }) })
}
export function useConfirmTermination(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async () => { const r = await apiClient.post(`/payroll/terminations/${id}/confirm/`); return r.data }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['terminations'] }); qc.invalidateQueries({ queryKey: ['employees'] }) } })
}
export function useUpdateTermination(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (d: any) => { const r = await apiClient.patch(`/payroll/terminations/${id}/`, d); return r.data }, onSuccess: () => qc.invalidateQueries({ queryKey: ['terminations'] }) })
}
export function useDeleteTermination(id: string) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async () => { await apiClient.delete(`/payroll/terminations/${id}/`); }, onSuccess: () => qc.invalidateQueries({ queryKey: ['terminations'] }) })
}

// ─── PayrollConfig (singleton por tenant) ───────────────────────────────────
export interface PayrollConfig {
  reminder_day: number
  reminder_enabled: boolean
  contract_expiry_alert_enabled?: boolean
  contract_expiry_alert_days?: number
  /** Ficha que da la 2.ª aprobación de vacaciones; null = una sola aprobación (#1751). */
  vacaciones_segundo_aprobador?: string | null
  vacaciones_segundo_aprobador_nombre?: string
  updated_at: string
}
export function usePayrollConfig() {
  return useQuery<PayrollConfig>({
    queryKey: ['payroll-config'],
    queryFn: async () => (await apiClient.get('/payroll/config/')).data,
  })
}
export function useUpdatePayrollConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (d: Partial<PayrollConfig>) => (await apiClient.patch('/payroll/config/', d)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-config'] }),
  })
}
